<template>
  <component :is="tag" v-bind="binding" v-on="$listeners" class="card">
    <div v-if="hasHeader" class="card__header">
      <slot name="header">
        <ImageComponent v-if="thumbnail" :src="thumbnail" :size="thumbnailSize" />
      </slot>
    </div>
    <div v-if="hasContent" class="card__content">
      <div v-if="title" class="card__title">{{ title }}</div>
      <slot />
    </div>
    <div v-if="hasFooter" class="card__footer">
      <div class="card__footer-content">
        <slot name="footer" />
      </div>
    </div>
    <slot name="root" />
  </component>
</template>

<script>
import ImageComponent from './Image'

export default {
  props: {
    tag: {
      type: String,
      default: 'div',
    },
    title: String,
    theme: {
      type: String,
      enums: ['default', 'border', 'flat'],
      default: 'default',
    },
    thumbnail: String,
    thumbnailSize: String,
  },
  computed: {
    binding() {
      return {
        ...this.$attrs,
        class: ['card--' + this.theme],
      }
    },
    hasHeader() {
      return this.$slots.header || this.thumbnail !== undefined
    },
    hasFooter() {
      return this.$slots.footer
    },
    hasContent() {
      return this.$slots.default
    },
  },
  components: {
    ImageComponent,
  },
}
</script>

<style lang="scss">
.card {
  --card-spacing: 1.75rem;
  $card-background: css-lighten('border-color', 9%);

  display: flex;
  flex-direction: column;
  border-radius: var(--border-radius-md);
  background-color: white;
  text-decoration: none;
  color: inherit;

  &--default {
    box-shadow: var(--box-shadow);
  }

  &--border {
    border: 1px solid var(--border-color);
  }

  &--disabled {
    &:hover {
      cursor: not-allowed;
    }

    .card__content {
      opacity: 0.3;
      pointer-events: none;
    }

    .card__footer {
      opacity: 0.3;
      pointer-events: none;
    }
  }

  &--flat {
  }

  &__header {
    min-height: 200px;
    background-color: $card-background;
    border-radius: var(--border-radius-md) var(--border-radius-md) 0 0;

    position: relative;

    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    &:first-child:last-child {
      flex-grow: 1;
    }

    & > .image {
      position: absolute;
      top: 0;
      bottom: 0;
      width: 100%;
      height: 100%;
      border-radius: inherit;
      overflow: hidden;
    }
  }

  &__title {
    font-weight: 700;
    line-height: 1;
    margin-bottom: 1rem;
    font-size: 1.4rem;
  }

  &__content,
  &__footer,
  &__header {
    padding: var(--card-spacing);
  }

  &__header + .card__footer {
    padding-top: var(--card-spacing);
  }

  &__content {
    flex-grow: 1;
  }

  &__footer {
    padding-top: 0;
    border-radius: 0 0 var(--border-radius-md) var(--border-radius-md);

    &-content {
      display: flex;
      flex-wrap: wrap;

      gap: 1rem;
    }
  }
}
</style>
